import { useState, useEffect, useRef } from "react";
import {
  Heading,
  TextArea,
  View,
  Text as SpectrumText,
  Image as SpectrumImage,
  Form,
  Flex,
  ActionButton,
  ProgressCircle,
  Tooltip,
  TooltipTrigger,
} from "@adobe/react-spectrum";
import { useSelector, useDispatch } from "react-redux";
import { askAi } from "../../../api/session-api";
import QuestionMark from "../../../images/questionMark.svg";
import SkeletonLoader from "../../Loader/SkeletonLoader";
import { RootState } from "../../../store/storeConfig";
// import SimpleChatFeedback from "./SimpleChatFeedback";
import { RefreshIcon } from "../../common/Icons";
import {
  addSimpleChatMessage,
  resetSimpleChat,
} from "../../../actions/simpleChat";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./simpleChatEditor.css";

interface ISimpleChatProps {
  title?: string;
  loader?: boolean;
}

const SimpleChatEditor = (props: ISimpleChatProps) => {
  const { title, loader } = props || {};
  const [value, setValue] = useState("");
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const chatContainerRef = useRef<any>(null);

  const dispatch = useDispatch();
  const simpleChat = useSelector((state: RootState) => state.simpleChat);
  const { messages } = simpleChat;

  // Handle text input change
  const handleChange = (val: string) => {
    setValue(val);
  };

  // Scroll to the bottom of the ai chat container when new messages are added
  useEffect(() => {
    if (chatContainerRef?.current) {
      const element = chatContainerRef.current;
      element.scrollTop = element.scrollHeight;
    }
  }, [messages?.length]);

  // Function to format AI responses with markdown
  const renderMarkdownResponse = (text: string) => {
    return (
      <div className="markdown-container" style={{ maxWidth: "100%" }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    );
  };

  // handle submit function to send the message to ask AI
  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (value && !isWaitingForResponse) {
      // Set waiting state to true
      setIsWaitingForResponse(true);

      // Generate a unique ID for the user message
      let uniqueUserResponseId = Math.random().toString(36).substring(7);

      // Add user message to state
      dispatch(
        addSimpleChatMessage(value, "", false, false, uniqueUserResponseId)
      );

      // Clear input field
      setValue("");

      // Call AI API with user's prompt directly
      askAi(value)
        .then((res) => {
          const { response } = res;
          // Generate a unique ID for AI response
          let uniqueAiResponseId = Math.random().toString(36).substring(7);

          // Add AI response to state
          dispatch(
            addSimpleChatMessage("", response, false, false, uniqueAiResponseId)
          );

          // Reset waiting state
          setIsWaitingForResponse(false);
        })
        .catch((error) => {
          console.log("Error while fetching ask ai response", error);
          // Reset waiting state even on error
          setIsWaitingForResponse(false);
        });
    }
  };

  // const handleFeedback = (messageId: string, isLiked: boolean, isDisliked: boolean) => {
  //   dispatch(addSimpleChatMessage(undefined, undefined, isLiked, isDisliked, messageId));
  // };

  const handleRestartChat = () => {
    if (isWaitingForResponse) {
      // If we're waiting for a response, just reset the waiting state
      setIsWaitingForResponse(false);
    }
    // Clear the input field
    setValue("");
    // Reset the chat messages in the store
    dispatch(resetSimpleChat());
  };

  return (
    <Flex
      direction={"column"}
      height={"100%"}
      UNSAFE_style={{ position: "relative" }}
    >
      <Flex
        direction={"column"}
        UNSAFE_style={{
          height: "calc(100% - 86px)",
          overflow: "auto",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
        }}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginX="size-100"
          marginY="size-100"
        >
          {messages.length > 0 && (
            <TooltipTrigger delay={0}>
              <ActionButton
                isQuiet
                onPress={handleRestartChat}
                UNSAFE_style={{
                  padding: "6px",
                }}
              >
                <RefreshIcon color="#000000" />
              </ActionButton>
              <Tooltip>Restart chat</Tooltip>
            </TooltipTrigger>
          )}
        </Flex>
        <div
          className="chat-ai-message chat-container"
          ref={chatContainerRef}
          style={{
            height: "calc(100% - 40px)",
            overflowY: "auto",
            padding: "10px",
          }}
        >
          {loader && (
            <View padding={10}>
              <SkeletonLoader width="100%" height="60px" marginTop="10px" />
            </View>
          )}
          {messages.length > 0 ? (
            <>
              {messages.map((message, index) => (
                <View
                  key={index}
                  UNSAFE_className={`chat-bubble ${
                    message.aiResponse ? "ai" : "user"
                  }`}
                  maxWidth={300}
                >
                  <View padding={10}>
                    <View>
                      {message.aiResponse && (
                        <>
                          <View maxWidth={300} borderRadius="small">
                            {renderMarkdownResponse(message.aiResponse)}
                          </View>
                          {/* <View marginTop={5}>
                            <SimpleChatFeedback 
                              message={message}
                              onFeedback={handleFeedback}
                            />
                          </View> */}
                        </>
                      )}
                    </View>
                    <View>
                      {message.userResponse && (
                        <View maxWidth={300} borderRadius="small">
                          <SpectrumText UNSAFE_className="case-ai-chat">
                            {message.userResponse}
                          </SpectrumText>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              ))}
              {isWaitingForResponse && (
                <View
                  UNSAFE_className="chat-bubble ai"
                  maxWidth={300}
                  padding={10}
                >
                  <View borderRadius="small">
                    <SpectrumText UNSAFE_className="case-ai-chat">
                      <Flex alignItems="center" gap="size-100">
                        <SpectrumText>AI is thinking</SpectrumText>
                        <ProgressCircle
                          size="S"
                          aria-label="Loading"
                          isIndeterminate
                        />
                      </Flex>
                    </SpectrumText>
                  </View>
                </View>
              )}
            </>
          ) : (
            !loader &&
            !isWaitingForResponse && (
              <Flex
                direction="column"
                justifyContent="center"
                alignItems="start"
                UNSAFE_style={{
                  height: "100%",
                  textAlign: "start",
                  margin: "0 auto",
                  maxWidth: "500px",
                }}
              >
                <Heading UNSAFE_className="case-ai-heading">
                  <SpectrumImage
                    width={"2rem"}
                    height={"2rem"}
                    marginBottom={16}
                    src={QuestionMark}
                    alt="question icon"
                  />
                  <SpectrumText>Ask AI for Assistance</SpectrumText>
                </Heading>
                <Flex
                  direction="column"
                  justifyContent="center"
                  alignItems="start"
                  width="100%"
                  gap="size-100"
                >
                  <SpectrumText UNSAFE_className="case-ai-info">
                    Got a customer issue or a question about iHub?
                  </SpectrumText>
                  <SpectrumText UNSAFE_className="case-ai-info">
                    Just ask — this chatbot is here to help. From support
                    queries to iHub-related problems, get instant guidance
                    anytime.
                  </SpectrumText>
                </Flex>
              </Flex>
            )
          )}
        </div>
      </Flex>
      <Flex
        direction={"column"}
        UNSAFE_style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "white",
        }}
      >
        <Form
          UNSAFE_className="ask-ai-chat-wrapper"
          validationBehavior="native"
          onSubmit={(e: any) => {
            handleSubmit(e);
          }}
          UNSAFE_style={{ padding: "15px" }}
        >
          <TextArea
            aria-label="Ask AI for Assistance"
            aria-labelledby="ask-ai"
            placeholder="Type your question here..."
            value={value}
            onChange={handleChange}
            height="56px"
            UNSAFE_className="ai-textarea-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isWaitingForResponse) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
          />

          <button
            type="submit"
            className="ai-chat-btn"
            onClick={isWaitingForResponse ? undefined : handleSubmit}
            disabled={isWaitingForResponse}
            style={{
              opacity: isWaitingForResponse ? 0.6 : 1,
              cursor: isWaitingForResponse ? "not-allowed" : "pointer",
            }}
          >
            Send
          </button>
        </Form>
      </Flex>
    </Flex>
  );
};

export default SimpleChatEditor;
